/**
 * NodeDetailPanel · P3 P0-4
 *
 * Right-column ws-panel detail view. When the user clicks a canvas node ·
 * the parent route lifts node_id state and renders this. Shows:
 *   - Header · node_id (truncated) · kind · status badge · close button
 *   - Run history · derived from task_run.* events for this node
 *   - Artifacts · derived from artifact.save events scoped to this node
 *
 * Pure read-only · doesn't mutate the audit store. Uses the same
 * audit-store selector pattern as CanvasView (zustand + useShallow).
 *
 * Out of scope (deferred to v0.2):
 *   - Inline run cancel / retry buttons (P1-7 approval flow has its own)
 *   - Live stdout / log streaming per run
 *   - Edge / dependency visualization
 */

import { useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { translateError, type TranslatedError } from '@aiw/util';
import { useAuditStore, selectSessionEvents } from '../stores/audit-store';
import type { WsEvent } from '../lib/ws-client';
import {
  eventsToNodes,
  eventsToEdges,
  eventsToArtifacts,
  eventsToRunsForNode,
  type CanvasNode,
  type CanvasArtifact,
  type CanvasNodeRun,
  type CanvasEdge,
  formatSize,
  presignArtifactDownload,
} from './CanvasView';
import {
  eventsToKnowledgeConflicts,
  eventsToKnowledgeItems,
  eventsToKnowledgeRevisions,
  knowledgeRefId,
  knowledgeUsageCounts,
  type CanvasKnowledgeConflict,
  type CanvasKnowledgeItem,
  type CanvasKnowledgeRevision,
  type KnowledgeResolution,
} from './KnowledgeContext';
import { useState, useRef } from 'react';

/**
 * Find the task_run that was active when a given audit was recorded.
 * Walks the event stream · returns the run_id of the most recent
 * task_run.created that has no terminal event before `auditId`. Used to
 * scope an output node's artifacts to the conversation that built it.
 * Pure · exported (potentially) for unit testing.
 */
function findEnclosingRunId(
  events: WsEvent[],
  auditId: number,
  workspaceId?: string,
): string | null {
  // Sort ASC for predictable scanning · ignore events without audit_id
  const sorted = [...events]
    .filter((e) => typeof e.audit_id === 'number' && Number.isSafeInteger(e.audit_id))
    .sort((a, b) => (a.audit_id! - b.audit_id!));
  // run_id → { startedAt: audit_id, endedAt: audit_id | null }
  const runs = new Map<string, { startedAt: number; endedAt: number | null }>();
  for (const e of sorted) {
    if (workspaceId && e.workspace_id !== workspaceId) continue;
    if (e.audit_id! > auditId) break; // future events don't change enclosing-run lookup
    const p: any = e.payload ?? {};
    const rid = typeof p.run_id === 'string' ? p.run_id : null;
    if (!rid) continue;
    if (e.kind === 'task_run.created') {
      if (!runs.has(rid)) runs.set(rid, { startedAt: e.audit_id!, endedAt: null });
    } else if (
      e.kind === 'task_run.finished' ||
      e.kind === 'task_run.error' ||
      e.kind === 'task_run.canceled' ||
      e.kind === 'task_run.skipped'
    ) {
      const cur = runs.get(rid);
      if (cur) cur.endedAt = e.audit_id!;
    }
  }
  // Find the run that started before auditId AND hasn't ended before auditId
  let best: { rid: string; startedAt: number } | null = null;
  for (const [rid, info] of runs.entries()) {
    if (info.startedAt > auditId) continue;
    if (info.endedAt !== null && info.endedAt < auditId) continue;
    if (!best || info.startedAt > best.startedAt) best = { rid, startedAt: info.startedAt };
  }
  return best?.rid ?? null;
}

const STATUS_LABEL: Record<CanvasNode['status'], string> = {
  dormant: '休眠',
  running: '运行中',
  thinking: '思考中',
  waiting: '等待',
  blocked: '阻塞',
  done: '完成',
  excluded: '已排除',
  dirty: '需重跑',
};

const KIND_LABEL: Record<CanvasNode['node_kind'], string> = {
  task: '任务',
  decision: '决策',
  human: '人类',
  output: '产出',
};

const RUN_STATUS_LABEL: Record<CanvasNodeRun['status'], string> = {
  queued: '排队中',
  running: '运行中',
  waiting: '等待',
  success: '成功',
  failed: '失败',
  canceled: '已取消',
};

const EDGE_KIND_LABEL: Record<CanvasEdge['edge_kind'], string> = {
  flow: '流程',
  data: '产物',
  dependency: '依赖',
};

const KNOWLEDGE_KIND_LABEL: Record<string, string> = {
  constraint: '约束',
  goal: '目标',
  insight: '洞察',
  decision: '决定',
  risk: '风险',
  file: '文件',
};

const KNOWLEDGE_STATUS_LABEL: Record<string, string> = {
  active: '可注入',
  superseded: '已替换',
  dismissed: '已忽略',
};

const KNOWLEDGE_RESOLUTION_LABEL: Record<KnowledgeResolution, string> = {
  overwrite: '采用新版',
  narrow: '缩窄新版',
  branch: '保留分支',
  keep_old: '保留旧版',
};

export interface CanvasLatestNodeRunSummary {
  run_id: string;
  output_id: string | null;
  audit_id: number;
  completed: boolean | null;
  source: 'node_output' | 'task_run.summary';
  source_audit_start: number | null;
  source_audit_end: number | null;
  changed_files: string[];
  artifacts: string[];
  key_decisions: string[];
  new_constraints: string[];
  risks: string[];
  open_questions: string[];
  handoff_summary: string | null;
}

interface NodeDependency {
  edge_id: string;
  edge_kind: CanvasEdge['edge_kind'];
  from_node_id: string;
  title: string;
  status: CanvasNode['status'] | null;
}

interface NodeDownstreamReference {
  key: string;
  node_id: string;
  title: string;
  status: CanvasNode['status'] | null;
  relation: 'edge' | 'context_ref';
  detail: string;
}

interface NodeKnowledgeView {
  items: Array<CanvasKnowledgeItem & { usage_count: number; revisions: CanvasKnowledgeRevision[] }>;
  conflicts: Array<CanvasKnowledgeConflict & {
    candidate: CanvasKnowledgeItem | null;
    existing: CanvasKnowledgeItem | null;
  }>;
}

function asStringArray(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((v): v is string => typeof v === 'string' && v.trim().length > 0).map((v) => v.trim())
    : [];
}

function asRefTextArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((v) => typeof v === 'string' ? v.trim() : contextRefText(v))
    .filter((v) => v.length > 0);
}

function textValue(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function intValue(value: unknown): number | null {
  return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0 ? value : null;
}

function objectValue(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};
}

function compactJson(value: unknown): string {
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

function contextRefText(ref: unknown): string {
  if (!ref || typeof ref !== 'object' || Array.isArray(ref)) return String(ref);
  const r = ref as Record<string, unknown>;
  return textValue(r.ref_id)
    ?? textValue(r.node_id)
    ?? textValue(r.output_id)
    ?? textValue(r.artifact_id)
    ?? textValue(r.path)
    ?? textValue(r.uri)
    ?? textValue(r.kind)
    ?? compactJson(ref);
}

function contextRefReason(ref: unknown): string | null {
  const r = objectValue(ref);
  return textValue(r.reason);
}

function contextRefMatchesNode(ref: unknown, node: CanvasNode): boolean {
  const candidates = new Set([node.node_id, node.planner_key].filter((v): v is string => Boolean(v)));
  if (typeof ref === 'string') return candidates.has(ref);
  const r = objectValue(ref);
  for (const key of ['node_id', 'source_node_id', 'ref_id', 'planner_key']) {
    const value = textValue(r[key]);
    if (value && candidates.has(value)) return true;
  }
  return false;
}

function buildLatestSummary(
  evt: WsEvent,
  source: CanvasLatestNodeRunSummary['source'],
): CanvasLatestNodeRunSummary | null {
  const payload = objectValue(evt.payload);
  const summary = objectValue(payload.summary);
  const output = objectValue(payload.output);
  const record = Object.keys(output).length > 0
    ? { ...payload, ...output }
    : Object.keys(summary).length > 0
      ? { ...payload, ...summary }
      : payload;
  const runId = textValue(record.run_id) ?? textValue(payload.run_id);
  if (!runId) return null;
  return {
    run_id: runId,
    output_id: textValue(record.output_id) ?? null,
    audit_id: evt.audit_id!,
    completed: typeof record.completed === 'boolean' ? record.completed : null,
    source,
    source_audit_start: intValue(record.source_audit_start),
    source_audit_end: intValue(record.source_audit_end),
    changed_files: asStringArray(record.changed_files),
    artifacts: asStringArray(record.artifacts).length > 0
      ? asStringArray(record.artifacts)
      : asRefTextArray(record.artifact_refs),
    key_decisions: asStringArray(record.key_decisions),
    new_constraints: asStringArray(record.new_constraints),
    risks: asStringArray(record.risks),
    open_questions: asStringArray(record.open_questions),
    handoff_summary: textValue(record.handoff_summary),
  };
}

export function eventsToLatestNodeRunSummary(
  events: WsEvent[],
  nodeId: string,
  workspaceId?: string,
): CanvasLatestNodeRunSummary | null {
  return eventsToLatestNodeRunSummaryForAny(events, [nodeId], workspaceId);
}

function eventsToLatestNodeRunSummaryForAny(
  events: WsEvent[],
  nodeIds: string[],
  workspaceId?: string,
): CanvasLatestNodeRunSummary | null {
  const wanted = new Set(nodeIds.filter(Boolean));
  if (wanted.size === 0) return null;
  let latest: CanvasLatestNodeRunSummary | null = null;
  const sorted = [...events].sort((a, b) => (a.audit_id ?? 0) - (b.audit_id ?? 0));
  for (const evt of sorted) {
    const supported =
      evt.kind === 'task_run.summary' ||
      evt.kind === 'node_output.recorded' ||
      evt.kind === 'node.output_recorded';
    if (!supported) continue;
    if (typeof evt.audit_id !== 'number' || !Number.isSafeInteger(evt.audit_id) || evt.audit_id <= 0) continue;
    if (workspaceId && evt.workspace_id !== workspaceId) continue;
    const payload = objectValue(evt.payload);
    const summary = objectValue(payload.summary);
    const output = objectValue(payload.output);
    const summaryNodeId =
      textValue(output.node_id) ??
      textValue(summary.node_id) ??
      textValue(payload.node_id);
    if (!summaryNodeId || !wanted.has(summaryNodeId)) continue;
    const next = buildLatestSummary(evt, evt.kind === 'task_run.summary' ? 'task_run.summary' : 'node_output');
    if (next) latest = next;
  }
  return latest;
}

function formatRelativeTime(ts: number, nowMs: number = Date.now()): string {
  const diffMs = nowMs - ts;
  if (diffMs < 0) return '刚刚';
  const sec = Math.floor(diffMs / 1000);
  if (sec < 60) return `${sec}s 前`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}min 前`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h 前`;
  const day = Math.floor(hr / 24);
  return `${day}d 前`;
}

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  const sec = Math.floor(ms / 1000);
  if (sec < 60) return `${sec}s`;
  const min = Math.floor(sec / 60);
  return `${min}min ${sec % 60}s`;
}

export interface NodeDetailPanelProps {
  sessionId: string;
  workspaceId?: string;
  /** Selected node_id · null = empty placeholder. */
  nodeId: string | null;
  /** Callback when user clicks the close button. */
  onClose?: () => void;
  /** Backend base URL · required for artifact downloads. */
  backendUrl?: string;
  /** SPA JWT · required for artifact downloads. */
  authToken?: string;
  /** Test-only · inject custom store. */
  storeForTest?: typeof useAuditStore;
  /** Test-only · inject fetch. */
  fetchImplForTest?: typeof fetch;
}

async function resolveKnowledgeConflict(opts: {
  backendUrl: string;
  authToken: string;
  sessionId: string;
  conflictId: string;
  resolution: KnowledgeResolution;
  fetchImpl?: typeof fetch;
}): Promise<{ ok: boolean; error?: string }> {
  const fi = opts.fetchImpl ?? globalThis.fetch;
  const url = `${opts.backendUrl.replace(/\/$/, '')}/api/sessions/${encodeURIComponent(opts.sessionId)}/knowledge/conflicts/${encodeURIComponent(opts.conflictId)}/resolve`;
  try {
    const resp = await fi(url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${opts.authToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ resolution: opts.resolution }),
    });
    if (!resp.ok) {
      let detail = `HTTP ${resp.status}`;
      try {
        const body = await resp.json() as { error?: string; error_code?: string };
        detail = body.error ?? body.error_code ?? detail;
      } catch { /* ignore */ }
      return { ok: false, error: detail };
    }
    return { ok: true };
  } catch (e: any) {
    return { ok: false, error: String(e?.message ?? e) };
  }
}

export function NodeDetailPanel({
  sessionId,
  workspaceId,
  nodeId,
  onClose,
  backendUrl,
  authToken,
  storeForTest,
  fetchImplForTest,
}: NodeDetailPanelProps): JSX.Element {
  const useStore = storeForTest ?? useAuditStore;
  const sessionEvents = useStore(useShallow(selectSessionEvents(sessionId)));
  const [knowledgeActionState, setKnowledgeActionState] = useState<Record<string, string>>({});

  const { node, nodes, runs, artifacts, dependencies, downstreamReferences, latestSummary, knowledge } = useMemo(() => {
    if (!nodeId) {
      return {
        node: null,
        nodes: [] as CanvasNode[],
        runs: [] as CanvasNodeRun[],
        artifacts: [] as CanvasArtifact[],
        dependencies: [] as NodeDependency[],
        downstreamReferences: [] as NodeDownstreamReference[],
        latestSummary: null as CanvasLatestNodeRunSummary | null,
        knowledge: { items: [], conflicts: [] } as NodeKnowledgeView,
      };
    }
    // codex R1 P2 fix · fail-closed when workspaceId missing · matches
    // CanvasView's artifact defense (A18 cross-tenant safety).
    if (!workspaceId) {
      return {
        node: null,
        nodes: [] as CanvasNode[],
        runs: [] as CanvasNodeRun[],
        artifacts: [] as CanvasArtifact[],
        dependencies: [] as NodeDependency[],
        downstreamReferences: [] as NodeDownstreamReference[],
        latestSummary: null as CanvasLatestNodeRunSummary | null,
        knowledge: { items: [], conflicts: [] } as NodeKnowledgeView,
      };
    }
    const allNodes = eventsToNodes(sessionEvents, workspaceId);
    const found = allNodes.find((n) => n.node_id === nodeId) ?? null;
    const allRuns = eventsToRunsForNode(sessionEvents, nodeId, workspaceId);
    const allArtifacts = eventsToArtifacts(sessionEvents, workspaceId);
    const allEdges = eventsToEdges(sessionEvents, workspaceId);
    const allKnowledgeItems = eventsToKnowledgeItems(sessionEvents, workspaceId);
    const allKnowledgeConflicts = eventsToKnowledgeConflicts(sessionEvents, workspaceId);
    const allKnowledgeRevisions = eventsToKnowledgeRevisions(sessionEvents, workspaceId);
    const usageCounts = knowledgeUsageCounts(allNodes);
    const byNodeId = new Map(allNodes.map((n) => [n.node_id, n]));
    const byKnowledgeId = new Map(allKnowledgeItems.map((item) => [item.item_id, item]));
    const dependencies = allEdges
      .filter((e) => e.to_node_id === nodeId)
      .map((e): NodeDependency => {
        const from = byNodeId.get(e.from_node_id);
        return {
          edge_id: e.edge_id,
          edge_kind: e.edge_kind,
          from_node_id: e.from_node_id,
          title: from?.title ?? `${e.from_node_id.slice(0, 8)}…`,
          status: from?.status ?? null,
        };
      });
    const downstreamReferences: NodeDownstreamReference[] = [];
    for (const e of allEdges.filter((edge) => edge.from_node_id === nodeId)) {
      const to = byNodeId.get(e.to_node_id);
      downstreamReferences.push({
        key: `edge:${e.edge_id}`,
        node_id: e.to_node_id,
        title: to?.title ?? `${e.to_node_id.slice(0, 8)}…`,
        status: to?.status ?? null,
        relation: 'edge',
        detail: EDGE_KIND_LABEL[e.edge_kind],
      });
    }
    if (found) {
      for (const candidate of allNodes) {
        if (candidate.node_id === nodeId) continue;
        candidate.context_refs.forEach((ref, idx) => {
          if (!contextRefMatchesNode(ref, found)) return;
          downstreamReferences.push({
            key: `ctx:${candidate.node_id}:${idx}`,
            node_id: candidate.node_id,
            title: candidate.title ?? `${candidate.node_id.slice(0, 8)}…`,
            status: candidate.status,
            relation: 'context_ref',
            detail: contextRefReason(ref) ?? contextRefText(ref),
          });
        });
      }
    }
    const outputUpstreamNodeIds = found?.node_kind === 'output'
      ? allEdges
        .filter((edge) =>
          edge.to_node_id === nodeId &&
          (edge.edge_kind === 'dependency' || edge.edge_kind === 'data' || edge.edge_kind === 'flow'))
        .map((edge) => edge.from_node_id)
      : [];
    const directLatestSummary = eventsToLatestNodeRunSummary(sessionEvents, nodeId, workspaceId);
    const latestSummary = directLatestSummary
      ?? (outputUpstreamNodeIds.length > 0
        ? eventsToLatestNodeRunSummaryForAny(sessionEvents, outputUpstreamNodeIds, workspaceId)
        : null);
    const scopedKnowledge = allKnowledgeItems
      .filter((item) =>
        item.scope_node_id === nodeId ||
        item.source_node_id === nodeId ||
        found?.context_refs.some((ref) => knowledgeRefId(ref) === item.item_id) === true,
      )
      .map((item) => ({
        ...item,
        usage_count: usageCounts.get(item.item_id) ?? 0,
        revisions: allKnowledgeRevisions.filter((revision) => revision.item_id === item.item_id),
      }));
    const scopedKnowledgeIds = new Set(scopedKnowledge.map((item) => item.item_id));
    const knowledge: NodeKnowledgeView = {
      items: scopedKnowledge,
      conflicts: allKnowledgeConflicts
        .filter((conflict) =>
          scopedKnowledgeIds.has(conflict.candidate_item_id) ||
          scopedKnowledgeIds.has(conflict.existing_item_id),
        )
        .map((conflict) => ({
          ...conflict,
          candidate: byKnowledgeId.get(conflict.candidate_item_id) ?? null,
          existing: byKnowledgeId.get(conflict.existing_item_id) ?? null,
        })),
    };

    // v0.1 user-feedback 2026-05-13 round 6 · unified artifact scoping.
    // ALL node kinds (task / output / decision / human) show the artifacts
    // produced during the same chat dispatch (task_run) that built this node.
    //
    // Why one rule for all kinds · user said 『每个任务都应该有对应的产出 ·
    // 在 task 运行的时候前置或者相关任务的产出物都会被利用到才对』. In
    // v0.1 the agent typically saves files at the end of the conversation
    // (when output node turns running) · so the file's audit_id is later
    // than the task nodes. Per-task time-window filtering would leave task
    // nodes empty. Showing run-scoped artifacts on every node matches the
    // mental model of 『everything this conversation produced』 · accessible
    // from any node in the tree.
    //
    // artifact.node_id (the dispatch placeholder id) never matches an
    // agent-created node anyway · so the old precise filter always returned
    // empty for task nodes. The enclosing-run filter is the right unified
    // model until v0.2 when agent passes explicit produces/consumes hints.
    void eventsToEdges; // kept for future v0.2 per-task scoping via dep walk
    const enclosingRunId = found
      ? findEnclosingRunId(sessionEvents, found.created_audit_id, workspaceId)
      : null;
    let nodeArtifacts: CanvasArtifact[];
    if (enclosingRunId) {
      nodeArtifacts = allArtifacts.filter((a) => a.run_id === enclosingRunId);
    } else {
      // Legacy / orphan node with no enclosing run · fall back to direct
      // node_id match (pre-refactor nodes that ARE the dispatch placeholder
      // do have artifact.node_id pointing at them).
      nodeArtifacts = allArtifacts.filter((a) => a.node_id === nodeId);
    }
    if (found?.node_kind === 'output' && nodeArtifacts.length === 0 && outputUpstreamNodeIds.length > 0) {
      const upstreamRunIds = new Set(
        outputUpstreamNodeIds
          .map((id) => eventsToLatestNodeRunSummary(sessionEvents, id, workspaceId)?.run_id)
          .filter((v): v is string => Boolean(v)),
      );
      const upstreamNodeIdSet = new Set(outputUpstreamNodeIds);
      nodeArtifacts = allArtifacts.filter((a) =>
        (a.node_id != null && upstreamNodeIdSet.has(a.node_id)) ||
        (a.run_id != null && upstreamRunIds.has(a.run_id)));
    }
    return { node: found, nodes: allNodes, runs: allRuns, artifacts: nodeArtifacts, dependencies, downstreamReferences, latestSummary, knowledge };
  }, [sessionEvents, workspaceId, nodeId]);

  async function handleKnowledgeResolution(conflictId: string, resolution: KnowledgeResolution): Promise<void> {
    if (!authToken) {
      setKnowledgeActionState((s) => ({ ...s, [conflictId]: 'error:Not logged in' }));
      return;
    }
    setKnowledgeActionState((s) => ({ ...s, [conflictId]: `resolving:${resolution}` }));
    const result = await resolveKnowledgeConflict({
      backendUrl: backendUrl ?? '',
      authToken,
      sessionId,
      conflictId,
      resolution,
      fetchImpl: fetchImplForTest,
    });
    setKnowledgeActionState((s) => ({
      ...s,
      [conflictId]: result.ok ? `resolved:${resolution}` : `error:${result.error ?? 'request failed'}`,
    }));
  }

  if (!nodeId) {
    return (
      <aside className="ws-panel panel" data-testid="node-detail-panel">
        <div className="panel-head">
          <span className="title">节点详情</span>
          <span className="grow" />
        </div>
        <div className="panel-body">
          <div className="ws-empty">
            选择一个节点<br />
            <span style={{ fontSize: 11 }}>点击画布上的节点查看运行 / 产出</span>
          </div>
        </div>
      </aside>
    );
  }

  if (!node) {
    return (
      <aside className="ws-panel panel" data-testid="node-detail-panel">
        <div className="panel-head">
          <span className="title">节点详情</span>
          <span className="grow" />
          {onClose ? (
            <button
              type="button"
              className="icon-btn"
              onClick={onClose}
              aria-label="关闭详情"
              style={{ fontSize: 18, padding: '0 8px', cursor: 'pointer' }}
            >
              ×
            </button>
          ) : null}
        </div>
        <div className="panel-body">
          <div className="ws-empty">
            未找到节点 <code>{nodeId.slice(0, 8)}…</code><br />
            <span style={{ fontSize: 11 }}>可能已被会话回滚</span>
          </div>
        </div>
      </aside>
    );
  }

  const latestRun = runs.length > 0 ? runs[runs.length - 1] : null;

  return (
    <aside className="ws-panel panel" data-testid="node-detail-panel">
      <div className="panel-head">
        <span className="title">节点详情</span>
        <span className="grow" />
        {onClose ? (
          <button
            type="button"
            className="icon-btn"
            onClick={onClose}
            aria-label="关闭详情"
            style={{ fontSize: 18, padding: '0 8px', cursor: 'pointer' }}
          >
            ×
          </button>
        ) : null}
      </div>
      <div className="panel-body" style={{ overflowY: 'auto', padding: '12px 16px' }}>
        <div data-testid="nd-header" style={{ marginBottom: 18 }}>
          <div style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 11,
            color: 'var(--fg-faint)',
            marginBottom: 4,
            wordBreak: 'break-all',
          }}>
            {node.node_id}
          </div>
          <div style={{ fontFamily: 'var(--font-title)', fontSize: 16, fontWeight: 700, marginBottom: 6 }}>
            {node.title ?? `${KIND_LABEL[node.node_kind]} 节点`}
          </div>
          <div style={{ fontSize: 11, color: 'var(--fg-soft)' }}>
            {KIND_LABEL[node.node_kind]} · {STATUS_LABEL[node.status]}
            {node.assigned_agent_id ? ` · @${node.assigned_agent_id}` : ''}
            {' · '}audit_id · {node.last_audit_id}
          </div>
          {node.description ? (
            <div style={{ fontSize: 12, color: 'var(--fg-soft)', marginTop: 8, lineHeight: 1.45 }}>
              {node.description}
            </div>
          ) : null}
        </div>

        <ExecutionMonitorStrip
          node={node}
          latestRun={latestRun}
          latestSummary={latestSummary}
        />

        <section data-testid="nd-acceptance" style={{ marginBottom: 18 }}>
          <h4 style={{
            fontFamily: 'var(--font-title)',
            fontSize: 13,
            fontWeight: 700,
            margin: '0 0 8px',
          }}>
            验收标准 <span style={{ color: 'var(--fg-faint)', fontWeight: 400 }}>({node.acceptance_criteria.length})</span>
          </h4>
          {node.acceptance_criteria.length === 0 ? (
            <div style={{ fontSize: 12, color: 'var(--fg-faint)', fontStyle: 'italic' }}>暂无验收标准</div>
          ) : (
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: 'var(--fg-soft)', lineHeight: 1.45 }}>
              {node.acceptance_criteria.map((item, idx) => (
                <li key={`${idx}:${item}`}>{item}</li>
              ))}
            </ul>
          )}
        </section>

        <section data-testid="nd-dependencies" style={{ marginBottom: 18 }}>
          <h4 style={{
            fontFamily: 'var(--font-title)',
            fontSize: 13,
            fontWeight: 700,
            margin: '0 0 8px',
          }}>
            依赖 <span style={{ color: 'var(--fg-faint)', fontWeight: 400 }}>({dependencies.length})</span>
          </h4>
          {dependencies.length === 0 ? (
            <div style={{ fontSize: 12, color: 'var(--fg-faint)', fontStyle: 'italic' }}>无上游依赖</div>
          ) : (
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
              {dependencies.map((dep) => (
                <li
                  key={dep.edge_id}
                  data-testid={`nd-dependency-${dep.edge_id}`}
                  style={{
                    border: '1px solid var(--border-default)',
                    borderRadius: 6,
                    padding: '6px 8px',
                    fontSize: 12,
                    color: 'var(--fg-soft)',
                  }}
                >
                  <span style={{ fontWeight: 700 }}>{dep.title}</span>
                  <span style={{ color: 'var(--fg-faint)' }}>
                    {' '}· {EDGE_KIND_LABEL[dep.edge_kind]}
                    {dep.status ? ` · ${STATUS_LABEL[dep.status]}` : ''}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section data-testid="nd-downstream-refs" style={{ marginBottom: 18 }}>
          <h4 style={{
            fontFamily: 'var(--font-title)',
            fontSize: 13,
            fontWeight: 700,
            margin: '0 0 8px',
          }}>
            下游引用 <span style={{ color: 'var(--fg-faint)', fontWeight: 400 }}>({downstreamReferences.length})</span>
          </h4>
          {downstreamReferences.length === 0 ? (
            <div style={{ fontSize: 12, color: 'var(--fg-faint)', fontStyle: 'italic' }}>暂无下游引用</div>
          ) : (
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
              {downstreamReferences.map((ref) => (
                <li
                  key={ref.key}
                  data-testid={`nd-downstream-${ref.node_id}`}
                  style={{
                    border: '1px solid var(--border-default)',
                    borderRadius: 6,
                    padding: '6px 8px',
                    fontSize: 12,
                    color: 'var(--fg-soft)',
                    background: ref.relation === 'context_ref' ? 'var(--bg-muted)' : 'var(--bg-base)',
                  }}
                >
                  <span style={{ fontWeight: 700 }}>{ref.title}</span>
                  <span style={{ color: 'var(--fg-faint)' }}>
                    {' '}· {ref.relation === 'edge' ? 'edge' : 'context'} · {ref.detail}
                    {ref.status ? ` · ${STATUS_LABEL[ref.status]}` : ''}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>

        {node.node_kind === 'decision' ? (
          <section data-testid="nd-branches" style={{ marginBottom: 18 }}>
            <h4 style={{
              fontFamily: 'var(--font-title)',
              fontSize: 13,
              fontWeight: 700,
              margin: '0 0 8px',
            }}>
              分支 <span style={{ color: 'var(--fg-faint)', fontWeight: 400 }}>({(node.branches ?? []).length})</span>
            </h4>
            {(node.branches ?? []).length === 0 ? (
              <div style={{ fontSize: 12, color: 'var(--fg-faint)', fontStyle: 'italic' }}>暂无分支</div>
            ) : (
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
                {(node.branches ?? []).map((branch) => {
                  const target = nodes.find((n) => n.node_id === branch.target_node_id);
                  return (
                    <li
                      key={`${branch.target_node_id}:${branch.state}`}
                      data-testid={`nd-branch-${branch.target_node_id}`}
                      style={{
                        border: '1px solid var(--border-default)',
                        borderRadius: 6,
                        padding: '6px 8px',
                        fontSize: 12,
                        color: 'var(--fg-soft)',
                        background: branch.state === 'active' || branch.state === 'parallel' ? 'var(--bg-muted)' : 'var(--bg-base)',
                      }}
                    >
                      <span style={{ fontWeight: 700 }}>{branch.label}</span>
                      <span style={{ color: 'var(--fg-faint)' }}>
                        {' '}· {branch.state}
                        {target?.title ? ` · ${target.title}` : ` · ${branch.target_node_id.slice(0, 8)}…`}
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        ) : null}

        {node.node_kind === 'output' && node.merge ? (
          <section data-testid="nd-merge" style={{ marginBottom: 18 }}>
            <h4 style={{
              fontFamily: 'var(--font-title)',
              fontSize: 13,
              fontWeight: 700,
              margin: '0 0 8px',
            }}>
              合并交接
            </h4>
            <div style={{
              border: '1px solid var(--border-default)',
              borderRadius: 6,
              padding: '8px 10px',
              background: 'var(--bg-base)',
              fontSize: 12,
              color: 'var(--fg-soft)',
              lineHeight: 1.45,
            }}>
              {node.merge.handoff_summary ? (
                <div data-testid="nd-merge-summary" style={{ marginBottom: 6 }}>{node.merge.handoff_summary}</div>
              ) : null}
              <div style={{ fontSize: 11, color: 'var(--fg-faint)' }}>
                sources {node.merge.source_node_ids.length} · artifacts {node.merge.artifact_refs.length}
              </div>
            </div>
          </section>
        ) : null}

        <section data-testid="nd-context-refs" style={{ marginBottom: 18 }}>
          <h4 style={{
            fontFamily: 'var(--font-title)',
            fontSize: 13,
            fontWeight: 700,
            margin: '0 0 8px',
          }}>
            上下文引用 <span style={{ color: 'var(--fg-faint)', fontWeight: 400 }}>({node.context_refs.length})</span>
          </h4>
          {node.context_refs.length === 0 ? (
            <div style={{ fontSize: 12, color: 'var(--fg-faint)', fontStyle: 'italic' }}>暂无引用</div>
          ) : (
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
              {node.context_refs.map((ref, idx) => {
                const required = Boolean(ref && typeof ref === 'object' && !Array.isArray(ref) && (ref as any).required === true);
                const reason = contextRefReason(ref);
                return (
                  <li
                    key={`${idx}:${contextRefText(ref)}`}
                    data-testid={`nd-context-ref-${idx}`}
                    style={{
                      border: '1px solid var(--border-default)',
                      borderRadius: 6,
                      padding: '6px 8px',
                      fontSize: 12,
                      color: 'var(--fg-soft)',
                      background: required ? 'var(--bg-muted)' : 'var(--bg-base)',
                    }}
                  >
                    <span style={{ fontWeight: 700 }}>{required ? 'required' : 'optional'}</span>
                    <span style={{ color: 'var(--fg-faint)' }}> · {contextRefText(ref)}</span>
                    {reason ? (
                      <div style={{ marginTop: 3, color: 'var(--fg-soft)' }}>{reason}</div>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        <section data-testid="nd-knowledge" style={{ marginBottom: 18 }}>
          <h4 style={{
            fontFamily: 'var(--font-title)',
            fontSize: 13,
            fontWeight: 700,
            margin: '0 0 8px',
          }}>
            知识资产 <span style={{ color: 'var(--fg-faint)', fontWeight: 400 }}>({knowledge.items.length})</span>
          </h4>
          {knowledge.items.length === 0 ? (
            <div style={{ fontSize: 12, color: 'var(--fg-faint)', fontStyle: 'italic' }}>暂无沉淀知识</div>
          ) : (
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
              {knowledge.items.map((item) => (
                <li
                  key={item.item_id}
                  data-testid={`nd-knowledge-${item.item_id}`}
                  style={{
                    border: '1px solid var(--border-default)',
                    borderRadius: 6,
                    padding: '8px 10px',
                    background: item.status === 'active' ? 'var(--bg-base)' : 'var(--bg-muted)',
                    fontSize: 12,
                    color: 'var(--fg-soft)',
                    lineHeight: 1.45,
                  }}
                >
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', marginBottom: 4 }}>
                    <span style={{ fontWeight: 700 }}>{KNOWLEDGE_KIND_LABEL[item.kind] ?? item.kind}</span>
                    <span style={{ color: 'var(--fg-faint)' }}>· {KNOWLEDGE_STATUS_LABEL[item.status] ?? item.status}</span>
                    <span style={{ color: 'var(--fg-faint)' }}>· 使用 {item.usage_count}</span>
                  </div>
                  <div>{item.content}</div>
                  <div style={{ marginTop: 5, fontSize: 11, color: 'var(--fg-faint)' }}>
                    source {item.source_kind}
                    {item.source_node_id ? ` · node ${item.source_node_id.slice(0, 8)}…` : ''}
                    {item.source_field ? ` · ${item.source_field}${item.source_index !== null ? `[${item.source_index}]` : ''}` : ''}
                    {item.source_audit_start !== null || item.source_audit_end !== null
                      ? ` · audit ${item.source_audit_start ?? '-'}-${item.source_audit_end ?? '-'}`
                      : ''}
                  </div>
                  {item.revisions.length > 0 ? (
                    <details style={{ marginTop: 6 }}>
                      <summary style={{ cursor: 'pointer', fontSize: 11, color: 'var(--fg-soft)' }}>
                        变更历史 {item.revisions.length}
                      </summary>
                      <ul style={{ listStyle: 'none', padding: 0, margin: '6px 0 0', display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {item.revisions.map((revision) => (
                          <li
                            key={revision.revision_id}
                            data-testid={`nd-knowledge-revision-${revision.revision_id}`}
                            style={{
                              borderLeft: '2px solid var(--border-default)',
                              paddingLeft: 8,
                              fontSize: 11,
                              color: 'var(--fg-faint)',
                            }}
                          >
                            {revision.reason ?? revision.editor_kind} · {KNOWLEDGE_STATUS_LABEL[revision.status] ?? revision.status}
                          </li>
                        ))}
                      </ul>
                    </details>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </section>

        {knowledge.conflicts.length > 0 ? (
          <section data-testid="nd-knowledge-conflicts" style={{ marginBottom: 18 }}>
            <h4 style={{
              fontFamily: 'var(--font-title)',
              fontSize: 13,
              fontWeight: 700,
              margin: '0 0 8px',
            }}>
              知识冲突 <span style={{ color: 'var(--fg-faint)', fontWeight: 400 }}>({knowledge.conflicts.length})</span>
            </h4>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
              {knowledge.conflicts.map((conflict) => {
                const state = knowledgeActionState[conflict.conflict_id];
                return (
                  <li
                    key={conflict.conflict_id}
                    data-testid={`nd-knowledge-conflict-${conflict.conflict_id}`}
                    style={{
                      border: '1px solid var(--accent-warning)',
                      borderRadius: 6,
                      padding: '8px 10px',
                      background: conflict.status === 'open' ? 'var(--bg-base)' : 'var(--bg-muted)',
                      fontSize: 12,
                      color: 'var(--fg-soft)',
                      lineHeight: 1.45,
                    }}
                  >
                    <div style={{ fontWeight: 700, marginBottom: 4 }}>
                      {conflict.status === 'open' ? '待确认' : '已处理'}
                      <span style={{ color: 'var(--fg-faint)', fontWeight: 400 }}> · {conflict.reason}</span>
                    </div>
                    {conflict.existing ? (
                      <div style={{ marginBottom: 3 }}>
                        <span style={{ color: 'var(--fg-faint)' }}>旧：</span>{conflict.existing.content}
                      </div>
                    ) : null}
                    {conflict.candidate ? (
                      <div>
                        <span style={{ color: 'var(--fg-faint)' }}>新：</span>{conflict.candidate.content}
                      </div>
                    ) : null}
                    {conflict.status === 'open' ? (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                        {(Object.keys(KNOWLEDGE_RESOLUTION_LABEL) as KnowledgeResolution[]).map((resolution) => (
                          <button
                            key={resolution}
                            type="button"
                            className="btn tiny"
                            disabled={!authToken || state?.startsWith('resolving:')}
                            onClick={() => { void handleKnowledgeResolution(conflict.conflict_id, resolution); }}
                          >
                            {KNOWLEDGE_RESOLUTION_LABEL[resolution]}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <div style={{ marginTop: 6, fontSize: 11, color: 'var(--fg-faint)' }}>
                        resolution {conflict.resolution ?? '-'}
                      </div>
                    )}
                    {state ? (
                      <div style={{ marginTop: 6, fontSize: 11, color: state.startsWith('error:') ? 'var(--accent-danger)' : 'var(--fg-faint)' }}>
                        {state.startsWith('error:') ? state.slice(6) : state.replace(':', ' · ')}
                      </div>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          </section>
        ) : null}

        <section data-testid="nd-latest-summary" style={{ marginBottom: 18 }}>
          <h4 style={{
            fontFamily: 'var(--font-title)',
            fontSize: 13,
            fontWeight: 700,
            margin: '0 0 8px',
          }}>
            最新总结
          </h4>
          {!latestSummary ? (
            <div style={{ fontSize: 12, color: 'var(--fg-faint)', fontStyle: 'italic' }}>暂无节点总结</div>
          ) : (
            <div style={{
              border: '1px solid var(--border-default)',
              borderRadius: 6,
              padding: '8px 10px',
              background: 'var(--bg-base)',
              fontSize: 12,
              color: 'var(--fg-soft)',
              lineHeight: 1.45,
            }}>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--fg-faint)', marginBottom: 4 }}>
                {latestSummary.source === 'node_output' ? 'node_output' : 'legacy summary'}
                {' · '}
                {latestSummary.output_id ? `${latestSummary.output_id.slice(0, 8)}… · ` : ''}
                {latestSummary.run_id.slice(0, 8)}… · audit_id · {latestSummary.audit_id}
              </div>
              {latestSummary.handoff_summary ? (
                <div data-testid="nd-latest-summary-text" style={{ marginBottom: 6 }}>
                  {latestSummary.handoff_summary}
                </div>
              ) : null}
              {latestSummary.source_audit_start !== null || latestSummary.source_audit_end !== null ? (
                <div style={{ fontSize: 11, color: 'var(--fg-faint)', marginBottom: 6 }}>
                  source audit {latestSummary.source_audit_start ?? '-'} - {latestSummary.source_audit_end ?? '-'}
                </div>
              ) : null}
              <MemoryList title="文件" items={latestSummary.changed_files} testId="nd-memory-files" />
              <MemoryList title="产出引用" items={latestSummary.artifacts} testId="nd-memory-artifacts" />
              <MemoryList title="关键决定" items={latestSummary.key_decisions} testId="nd-memory-decisions" />
              <MemoryList title="新增约束" items={latestSummary.new_constraints} testId="nd-memory-constraints" />
              <MemoryList title="风险" items={latestSummary.risks} testId="nd-memory-risks" />
              <MemoryList title="未决问题" items={latestSummary.open_questions} testId="nd-memory-questions" />
            </div>
          )}
        </section>

        <section data-testid="nd-runs" style={{ marginBottom: 18 }}>
          <h4 style={{
            fontFamily: 'var(--font-title)',
            fontSize: 13,
            fontWeight: 700,
            margin: '0 0 8px',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
          }}>
            运行 <span style={{ color: 'var(--fg-faint)', fontWeight: 400 }}>({runs.length})</span>
          </h4>
          {runs.length === 0 ? (
            <div style={{ fontSize: 12, color: 'var(--fg-faint)', fontStyle: 'italic' }}>
              暂无运行记录
            </div>
          ) : (
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
              {runs.map((r) => (
                <li
                  key={r.run_id}
                  data-testid={`nd-run-${r.run_id}`}
                  style={{
                    border: '1px solid var(--border-default)',
                    borderRadius: 6,
                    padding: '8px 10px',
                    background: 'var(--bg-base)',
                    fontSize: 12,
                  }}
                >
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 4,
                  }}>
                    <code style={{ fontSize: 10, color: 'var(--fg-soft)' }}>{r.run_id.slice(0, 8)}…</code>
                    <span data-testid="run-status" style={{
                      fontSize: 10,
                      padding: '1px 6px',
                      borderRadius: 4,
                      background:
                        r.status === 'success' ? 'var(--accent-success)' :
                        r.status === 'failed' ? 'var(--accent-danger)' :
                        r.status === 'canceled' ? 'var(--bg-muted)' :
                        r.status === 'running' ? 'var(--accent-warning)' :
                        'var(--bg-muted)',
                      color: r.status === 'success' || r.status === 'failed' || r.status === 'running' ? '#fff' : 'var(--fg-default)',
                    }}>
                      {RUN_STATUS_LABEL[r.status]}
                    </span>
                  </div>
                  <div style={{ color: 'var(--fg-soft)', fontSize: 11 }}>
                    {r.agent_id ? <>agent · <b>{r.agent_id}</b> · </> : null}
                    {formatRelativeTime(r.created_ts)}
                    {r.ended_ts ? <> · 用时 {formatDuration(r.ended_ts - r.created_ts)}</> : null}
                  </div>
                  {r.status === 'failed' && r.error_detail ? (
                    <RunErrorTranslation detail={r.error_detail} />
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </section>

        <details data-testid="nd-planner-metadata" style={{ marginBottom: 18, fontSize: 12, color: 'var(--fg-soft)' }}>
          <summary style={{ cursor: 'pointer', fontFamily: 'var(--font-title)', fontSize: 13, fontWeight: 700 }}>
            Planner metadata
          </summary>
          <div style={{
            marginTop: 8,
            border: '1px solid var(--border-default)',
            borderRadius: 6,
            padding: '8px 10px',
            background: 'var(--bg-base)',
            fontFamily: 'var(--font-mono)',
            fontSize: 10,
            lineHeight: 1.55,
            wordBreak: 'break-word',
          }}>
            <div>planner_key: {node.planner_key ?? '-'}</div>
            <div>plan_revision: {node.plan_revision ?? '-'}</div>
            <div>source_audit: {node.source_audit_start ?? '-'} - {node.source_audit_end ?? '-'}</div>
            <div>superseded_at: {node.superseded_at ?? '-'}</div>
            <div>boundaries: {node.boundaries == null ? '-' : compactJson(node.boundaries)}</div>
          </div>
        </details>

        <section data-testid="nd-artifacts">
          <h4 style={{
            fontFamily: 'var(--font-title)',
            fontSize: 13,
            fontWeight: 700,
            margin: '0 0 8px',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
          }}>
            产出 <span style={{ color: 'var(--fg-faint)', fontWeight: 400 }}>({artifacts.length})</span>
          </h4>
          {artifacts.length === 0 ? (
            <div style={{ fontSize: 12, color: 'var(--fg-faint)', fontStyle: 'italic' }}>
              暂无产出
            </div>
          ) : (
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
              {artifacts.map((a) => (
                <ArtifactRow
                  key={`${a.path}:${a.audit_id}`}
                  artifact={a}
                  backendUrl={backendUrl}
                  authToken={authToken}
                  fetchImpl={fetchImplForTest}
                />
              ))}
            </ul>
          )}
        </section>
      </div>
    </aside>
  );
}

function formatRunCost(run: CanvasNodeRun | null): string {
  if (!run) return 'cost · -';
  const tokensIn = run.cost_tokens_in ?? 0;
  const tokensOut = run.cost_tokens_out ?? 0;
  const tokens = tokensIn + tokensOut;
  if (tokens === 0 && !run.cost_usd_micros) return 'cost · 等待上报';
  const parts = [`${tokens} tokens`];
  if (run.cost_usd_micros && run.cost_usd_micros > 0) {
    parts.push(`$${(run.cost_usd_micros / 1_000_000).toFixed(4)}`);
  }
  return parts.join(' · ');
}

function monitorProblem(node: CanvasNode, latestRun: CanvasNodeRun | null, latestSummary: CanvasLatestNodeRunSummary | null): string {
  if (latestRun?.status === 'failed' && latestRun.error_detail) return latestRun.error_detail;
  if (node.status === 'blocked') return '节点阻塞，查看运行错误或补充上下文';
  if (latestSummary && latestSummary.open_questions.length > 0) return `${latestSummary.open_questions.length} 个未决问题`;
  if (latestSummary && latestSummary.risks.length > 0) return `${latestSummary.risks.length} 个风险`;
  return '无阻塞';
}

function ExecutionMonitorStrip({
  node,
  latestRun,
  latestSummary,
}: {
  node: CanvasNode;
  latestRun: CanvasNodeRun | null;
  latestSummary: CanvasLatestNodeRunSummary | null;
}): JSX.Element {
  const action = latestRun
    ? RUN_STATUS_LABEL[latestRun.status]
    : STATUS_LABEL[node.status];
  return (
    <section
      data-testid="nd-monitor-strip"
      style={{
        border: '1px solid var(--border-default)',
        borderRadius: 6,
        background: 'var(--bg-muted)',
        padding: '8px 10px',
        marginBottom: 18,
        display: 'grid',
        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        gap: 8,
        fontSize: 11,
        color: 'var(--fg-soft)',
      }}
    >
      <MonitorCell label="agent" value={latestRun?.agent_id ?? node.assigned_agent_id ?? '-'} />
      <MonitorCell label="action" value={action} />
      <MonitorCell label="cost" value={formatRunCost(latestRun)} />
      <MonitorCell label="problem" value={monitorProblem(node, latestRun, latestSummary)} />
    </section>
  );
}

function MonitorCell({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontFamily: 'var(--font-mono)', color: 'var(--fg-faint)', marginBottom: 2 }}>{label}</div>
      <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={value}>{value}</div>
    </div>
  );
}

function MemoryList({
  title,
  items,
  testId,
}: {
  title: string;
  items: string[];
  testId: string;
}): JSX.Element | null {
  if (items.length === 0) return null;
  return (
    <div data-testid={testId} style={{ marginTop: 8 }}>
      <div style={{ fontSize: 11, color: 'var(--fg-faint)', marginBottom: 3 }}>{title}</div>
      <ul style={{ margin: 0, paddingLeft: 16 }}>
        {items.map((item, idx) => (
          <li key={`${idx}:${item}`} style={{ marginBottom: 2 }}>{item}</li>
        ))}
      </ul>
    </div>
  );
}

interface ArtifactRowProps {
  artifact: CanvasArtifact;
  backendUrl?: string;
  authToken?: string;
  fetchImpl?: typeof fetch;
}

function ArtifactRow({ artifact, backendUrl, authToken, fetchImpl }: ArtifactRowProps): JSX.Element {
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inflightRef = useRef(false);
  const filename = artifact.path.split('/').filter(Boolean).pop() ?? artifact.path;
  const canDownload = Boolean(backendUrl && authToken);

  async function handleDownload(): Promise<void> {
    if (inflightRef.current) return;
    if (!backendUrl || !authToken) {
      setError('not configured · login required');
      return;
    }
    inflightRef.current = true;
    setDownloading(true);
    setError(null);
    try {
      const result = await presignArtifactDownload({
        backendUrl,
        authToken,
        hash: artifact.hash,
        filename,
        fetchImpl,
      });
      if (!result.ok || !result.url) {
        setError(result.error ?? 'unknown error');
        return;
      }
      // Synthetic anchor-click pattern · avoids popup blocker · same as
      // CanvasView ArtifactCard (codex P2.8.D1 R1 P1 fix).
      if (typeof document === 'undefined') {
        setError('download not supported in this environment');
        return;
      }
      try {
        const a = document.createElement('a');
        a.href = result.url;
        a.download = filename;
        a.target = '_blank';
        a.rel = 'noopener noreferrer';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } catch (e: any) {
        setError(String(e?.message ?? e));
      }
    } catch (e: any) {
      setError(String(e?.message ?? e));
    } finally {
      setDownloading(false);
      inflightRef.current = false;
    }
  }

  return (
    <li
      data-testid={`nd-artifact-${artifact.path}`}
      style={{
        border: '1px solid var(--border-default)',
        borderRadius: 6,
        padding: '8px 10px',
        background: 'var(--bg-base)',
        fontSize: 12,
      }}
    >
      <div style={{
        fontFamily: 'var(--font-title)',
        fontWeight: 700,
        wordBreak: 'break-word',
        marginBottom: 2,
      }}>
        {filename}
      </div>
      <div style={{
        fontFamily: 'var(--font-mono)',
        fontSize: 10,
        color: 'var(--fg-soft)',
        wordBreak: 'break-all',
        marginBottom: 4,
      }}>
        {artifact.path}
      </div>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        fontSize: 10,
        color: 'var(--fg-faint)',
      }}>
        <span>{formatSize(artifact.size)}</span>
        <button
          type="button"
          onClick={handleDownload}
          disabled={!canDownload || downloading}
          aria-label={`下载 ${filename}`}
          style={{
            fontSize: 10,
            padding: '2px 8px',
            border: '1px solid var(--border-default)',
            borderRadius: 4,
            background: canDownload && !downloading ? 'var(--bg-base)' : 'var(--bg-muted)',
            cursor: canDownload && !downloading ? 'pointer' : 'not-allowed',
          }}
        >
          {downloading ? '下载中…' : '下载'}
        </button>
      </div>
      {error ? (
        <div style={{ fontSize: 10, color: 'var(--accent-danger)', marginTop: 4 }}>{error}</div>
      ) : null}
    </li>
  );
}

/**
 * P3.14 · 错误翻译卡片 · 给 failed 运行行内插的中文翻译 + 建议动作 +
 * raw 详情折叠。Pure presentational · 调用 `@aiw/util` translateError 做
 * 分类（9 类 + unknown）· 不抛 · 不副作用。
 */
function RunErrorTranslation({ detail }: { detail: string }): JSX.Element {
  const [showRaw, setShowRaw] = useState(false);
  const t: TranslatedError = useMemo(() => translateError(detail), [detail]);
  return (
    <div
      data-testid="run-error-translation"
      style={{
        marginTop: 6,
        padding: '6px 8px',
        background: 'var(--bg-muted)',
        border: '1px solid var(--accent-danger)',
        borderRadius: 4,
        fontSize: 11,
      }}
    >
      <div
        data-testid="run-error-message-zh"
        style={{ color: 'var(--accent-danger)', fontWeight: 700, marginBottom: 2 }}
      >
        {t.messageZh}
      </div>
      <div
        data-testid="run-error-action"
        style={{ color: 'var(--fg-soft)', marginBottom: 4 }}
      >
        建议：{t.suggestedAction}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 10 }}>
        <span
          data-testid="run-error-kind"
          style={{
            padding: '1px 5px',
            borderRadius: 3,
            background: t.retryable ? 'var(--accent-warning)' : 'var(--bg-base)',
            color: t.retryable ? '#fff' : 'var(--fg-soft)',
            border: t.retryable ? 'none' : '1px solid var(--border-default)',
            fontFamily: 'var(--font-mono)',
          }}
        >
          {t.kind}
        </span>
        <span style={{ color: 'var(--fg-faint)' }}>
          {t.retryable ? '可重试' : '不可重试'}
        </span>
        <span style={{ flex: 1 }} />
        <button
          type="button"
          onClick={() => setShowRaw((v) => !v)}
          aria-expanded={showRaw}
          style={{
            fontSize: 10,
            padding: '1px 6px',
            border: '1px solid var(--border-default)',
            borderRadius: 3,
            background: 'var(--bg-base)',
            cursor: 'pointer',
          }}
        >
          {showRaw ? '收起详情' : '展开详情'}
        </button>
      </div>
      {showRaw ? (
        <pre
          data-testid="run-error-raw"
          style={{
            marginTop: 6,
            padding: 6,
            background: 'var(--bg-base)',
            border: '1px solid var(--border-default)',
            borderRadius: 3,
            fontSize: 10,
            fontFamily: 'var(--font-mono)',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-all',
            color: 'var(--fg-default)',
          }}
        >
          {t.rawCode ? `${t.rawCode}: ` : ''}{t.rawMessage || detail}
        </pre>
      ) : null}
    </div>
  );
}
